/*
* @Author: Gang Jiang
* @Date: 2026-03-06
* @Last Modified By: Gang Jiang
* @Last Modified Time: 2026-06-05 16:00:00
*/

<template>
  <div class="pending-payment">
    <div class="filter filter-align">
      <a-form :model="filter" layout="horizontal" labelAlign="right" :labelCol="filterLabelCol">
        <a-row :gutter="[30, 0]" align="middle">
          <a-col :xs="24" :sm="12" :md="8">
            <a-form-item label="Influencer Username" :labelCol="filterLabelCol">
              <a-auto-complete v-model:value="influencerUsernameInput" :allowClear="true" placeholder="Please enter"
                :options="userNameSuggestions" @select="onUserSelect" @keydown.enter.stop="changeUser"
                @clear="clearUser" @search="onUserSearch" />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8">
            <a-form-item label="Influencer Email" :labelCol="filterLabelCol">
              <a-input v-model:value="filter.influencerEmail" placeholder="Please enter" allow-clear />
            </a-form-item>

          </a-col>
          <a-col :xs="24" :sm="12" :md="8">
            <a-form-item label="Payment Date" :labelCol="filterLabelCol">
              <a-range-picker v-model:value="filterDate" :locale="enGB" class="filter-date-picker" format="YYYY-MM-DD"
                :allowClear="true" :placeholder="['Start week', 'End week']" :disabled-date="disabledPaymentRangeDate"
                @open-change="onPaymentRangeOpenChange" @calendar-change="onPaymentRangeCalendarChange"
                @change="onPaymentRangeChange" />
            </a-form-item>
          </a-col>
        </a-row>
        <a-row :gutter="[30, 0]" align="middle" class="filter-row-2">
          <a-col :xs="24" :sm="12" :md="8">
            <a-form-item label="Status" :labelCol="filterLabelCol">
              <a-select
                v-model:value="filter.status"
                placeholder="Select"
                :options="statusOptions"
                style="width: 100%"
              />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="24" :md="16" class="btns">
            <a-button type="primary" @click="handleSearch">Search</a-button>
            <a-button @click="handleReset">Reset</a-button>
            <a :href="exportUrl" download>
              <a-button type="primary">Export</a-button>
            </a>
          </a-col>
        </a-row>
      </a-form>
    </div>

    <div class="table-wrapper">
      <a-spin :spinning="isLoading">
        <a-table class="payments-table" :data-source="paymentsList" :columns="columns" :scroll="{ x: 2000 }"
          size="small" row-key="paymentId" :row-selection="rowSelection" :pagination="tablePagination"
          :showSorterTooltip="false" @change="handleTableChange">
          <template #headerCell="{ column }">
            <TableTitlePopover v-if="column.titleTip" :title="column.key" :content="column.titleTip" />
            <span v-else>{{ column.key || column.title }}</span>
          </template>

          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'kolPaymentPeriod'">
              {{ formatPt(record.paymentPeriodStartTime) }} ~
              {{ formatPt(record.paymentPeriodEndTime) }}
            </template>

            <template v-else-if="
              column.dataIndex === 'affiliatePaymentAmountUsd' ||
              column.dataIndex === 'paymentAmountUsdForKol' ||
              column.dataIndex === 'adjustmentAmountUsdForKol' ||
              column.dataIndex === 'actualPayoutUsdForKol'
            ">
              <span v-if="column.dataIndex === 'adjustmentAmountUsdForKol'">
                <span>{{ formatCurrency(record.adjustmentAmountUsdForKol, 'USD') }}</span>
                <a-tooltip v-if="record.adjustmentReason" placement="top">
                  <a-tooltip placement="top" overlayClassName="white-bg-tooltip">
                    <template #title>
                      <div>{{ record.adjustmentReason || '' }}</div>
                      <div>{{ formatPt(record.adjustmentTime, 'MMMM D, YYYY') }}</div>
                    </template>
                    <QuestionCircleFilled style="margin-left: 4px;color: #000000a6;" />
                  </a-tooltip>
                </a-tooltip>
              </span>
              <span v-else-if="column.dataIndex === 'affiliatePaymentAmountUsd'">
                <span>
                  {{ formatCurrency(record.affiliatePaymentAmountUsd, 'USD') }}
                </span>
              </span>
              <span v-else-if="column.dataIndex === 'paymentAmountUsdForKol'">
                {{ formatCurrency(record.paymentAmountUsdForKol, 'USD') }}
              </span>
              <span v-else>
                {{ formatCurrency(record.actualPayoutUsdForKol, 'USD') }}
              </span>
            </template>

            <template v-else-if="column.dataIndex === 'paymentAccount'">
              <span v-if="record.paymentAccount" class="paypal-account-text">
                {{ record.paymentAccount }}
              </span>
              <span v-else>-</span>
            </template>

            <template v-else-if="column.key === 'Status'">
              <span class="status-tag" :class="record.status
                ? record.status.replace(' ', '-').toLowerCase()
                : ''
                ">
                {{ record.status || '-' }}
              </span>
            </template>

            <template v-else-if="column.key === 'Action'">
              <div class="action-links">
                <span
                  v-if="record.status === 'Confirmed' && record.paid && hasPaymentAccount(record)"
                  class="action-paid"
                >Paid</span>
                <a
                  v-else-if="record.status === 'Confirmed' && hasPaymentAccount(record)"
                  href="javascript:;"
                  @click.prevent="openPayModal(record)"
                >
                  Pay
                </a>
                <a-popconfirm v-if="record.status === 'Pending'"
                  title="After confirmation, the commission status will go to Pending Payment. Are you sure to confirm it?"
                  :align="{ points: ['br', 'tr'], offset: [-50, 8] }" overlayClassName="pending-confirm-popconfirm"
                  :overlayStyle="{ width: '300px', maxWidth: '300px' }" :okButtonProps="{ size: 'middle' }"
                  :cancelButtonProps="{ size: 'middle' }" @confirm="handleConfirm(record)">
                  <a href="javascript:;" @click.prevent>Confirm</a>
                </a-popconfirm>
                <a-popconfirm
                  v-if="record.status !== 'Confirmed'"
                  :open="isAdjustPopOpen(record)"
                  :align="{ points: ['br', 'tr'], offset: [-50, 8] }"
                  overlay-class-name="pending-adjust-popconfirm"
                  :overlay-inner-style="{ overflow: 'visible' }"
                  :ok-button-props="{ size: 'middle' }"
                  :cancel-button-props="{ size: 'middle' }"
                  :overlay-style="{ minWidth: '400px', maxWidth: '480px' }"
                  :destroy-tooltip-on-hide="true"
                  :icon="false"
                  ok-text="OK"
                  @open-change="(open) => onAdjustPopOpenChange(open, record)"
                  @confirm="handleAdjustPopConfirm"
                >
                  <template #title>
                    <span>Adjustments to KOL Payment</span>
                  </template>
                  <template #description>
                    <div
                      class="pending-adjust-popconfirm__form"
                      @mousedown.stop
                    >
                      <div class="pending-adjust-popconfirm__field">
                        <div class="pending-adjust-popconfirm__label">Add amount</div>
                        <div class="adjust-amount-row">
                          <a-select
                            v-model:value="adjustForm.type"
                            style="width: 160px"
                            popup-class-name="pending-adjust-select-dropdown"
                            :get-popup-container="getAdjustSelectPopupContainer"
                            :options="[
                              { label: 'Add amount', value: 'add' },
                              { label: 'Subtract amount', value: 'subtract' },
                            ]"
                            @dropdown-visible-change="onAdjustSelectDropdownVisibleChange"
                          />
                          <a-input
                            v-model:value="adjustForm.amount"
                            prefix="$"
                            placeholder="0.00"
                            style="flex: 1; margin-left: 12px"
                          />
                        </div>
                      </div>
                      <div class="pending-adjust-popconfirm__field">
                        <div class="pending-adjust-popconfirm__label">Reasons for the adjustment</div>
                        <a-textarea
                          v-model:value="adjustForm.reason"
                          :rows="2"
                          placeholder="Please enter"
                        />
                      </div>
                    </div>
                  </template>
                  <a href="javascript:;" @click.prevent>Adjust</a>
                </a-popconfirm>
                <a :href="`/#/payments/pending-payment/${record.paymentId}`" target="_blank">Details</a>
                <a :class="record.payable ? '' : 'text-danger'" :href="`/#/influencer/edit/${record.influencerId}?s=p`"
                  target="_blank">Pay Info</a>
              </div>
            </template>

            <template v-else-if="column.dataIndex === 'note'">
              <a-input v-model:value="record.note" placeholder="Enter note for payment for KOL" allow-clear
                @blur="(e) => handleNoteChange(record, e.target?.value)"
                @pressEnter="(e) => handleNoteChange(record, e.target?.value)" />
            </template>

            <template v-else>
              {{ record[column.dataIndex] ?? '-' }}
            </template>
          </template>
        </a-table>
      </a-spin>
    </div>

    <div v-if="selectedRowKeys.length" class="pending-batch-bar">
      <div class="pending-batch-bar__left">
        <span class="pending-batch-bar__badge">{{ selectedRowKeys.length }}</span>
        <span class="pending-batch-bar__label">Selected Payout(s)</span>
      </div>
      <span class="pending-batch-bar__divider" aria-hidden="true" />
      <div class="pending-batch-bar__amount">
        <span class="pending-batch-bar__amount-label">Cumulative Amount:</span>
        <span class="pending-batch-bar__amount-value">{{ formatCurrency(selectedCumulativeAmount, 'USD') }}</span>
      </div>
      <a-button type="primary" class="pending-batch-bar__btn" @click="openBatchPayModal">
        Batch Pay
      </a-button>
    </div>

    <a-modal v-model:open="payModalVisible" :footer="null" :width="520" :maskClosable="false" destroyOnClose
      wrapClassName="kol-payout-confirm-modal" @cancel="closePayModal">
      <div class="kol-payout-confirm-modal__title">Confirm KOL Payout</div>
      <p class="kol-payout-confirm-modal__question">
        Are you sure you want to mark this Payment ID
        <strong>{{ payTargetRecord?.paymentId ?? '-' }}</strong>
        as Paid?
      </p>
      <div class="kol-payout-confirm-modal__panel">
        <div class="kol-payout-confirm-modal__row">
          <span class="kol-payout-confirm-modal__label">KOL Username</span>
          <span class="kol-payout-confirm-modal__value kol-payout-confirm-modal__value--strong">
            {{ payDisplay.username }}
          </span>
        </div>
        <div class="kol-payout-confirm-modal__row">
          <span class="kol-payout-confirm-modal__label">PayPal Account</span>
          <span class="kol-payout-confirm-modal__value kol-payout-confirm-modal__value--link">
            {{ payDisplay.paypalAccount }}
          </span>
        </div>
        <div class="kol-payout-confirm-modal__row">
          <span class="kol-payout-confirm-modal__label">Payout Amount</span>
          <span class="kol-payout-confirm-modal__value kol-payout-confirm-modal__value--amount">
            {{ payDisplay.payoutAmount }}
          </span>
        </div>
      </div>
      <div class="kol-payout-confirm-modal__warning">
        <WarningFilled class="kol-payout-confirm-modal__warning-icon" />
        <span class="kol-payout-confirm-modal__warning-text">
          Warning: This action is irreversible on the server. The KOL payment status and all
          associated commission transactions will instantly transition to "Paid".
        </span>
      </div>
      <div class="kol-payout-confirm-modal__footer">
        <a-button @click="closePayModal">Cancel</a-button>
        <a-button type="primary" :loading="paySubmitting" @click="handlePayConfirm">Confirm</a-button>
      </div>
    </a-modal>

    <a-modal v-model:open="batchPayModalVisible" :footer="null" :width="720" :maskClosable="!batchPaySubmitting"
      :closable="!batchPaySubmitting" destroyOnClose wrapClassName="batch-checkout-modal" @cancel="closeBatchPayModal">
      <div class="batch-checkout-modal__title">
        Batch Checkout ({{ selectedRowKeys.length }} items)
      </div>
      <p class="batch-checkout-modal__desc">
        You have selected
        <strong>{{ selectedRowKeys.length }}</strong>
        pending KOL payments. Please review the financial checkout breakdown:
      </p>
      <a-table class="batch-checkout-modal__table" :data-source="selectedBatchRecords" :columns="batchCheckoutColumns"
        :pagination="false" size="small" row-key="paymentId" :scroll="{ y: 280 }">
        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'paymentAccount'">
            <span class="batch-checkout-modal__paypal">
              {{ record.paymentAccount || '-' }}
            </span>
          </template>
          <template v-else-if="column.dataIndex === 'actualPayoutUsdForKol'">
            <span class="batch-checkout-modal__payout">
              {{ formatCurrency(record.actualPayoutUsdForKol, 'USD') }}
            </span>
          </template>
        </template>
      </a-table>
      <div class="batch-checkout-modal__total">
        <span class="batch-checkout-modal__total-label">Total Checkout Amount:</span>
        <span class="batch-checkout-modal__total-value">
          {{ formatCurrency(selectedCumulativeAmount, 'USD') }}
        </span>
      </div>
      <div class="batch-checkout-modal__notice">
        <WarningFilled class="batch-checkout-modal__notice-icon" />
        <span class="batch-checkout-modal__notice-text">
          Notice: Payout transactions will execute sequentially with a 400ms buffer delay.
          Keep this tab open during execution.
        </span>
      </div>
      <div class="batch-checkout-modal__footer">
        <a-button :disabled="batchPaySubmitting" @click="closeBatchPayModal">Cancel</a-button>
        <a-button type="primary" :loading="batchPaySubmitting" @click="handleBatchPayConfirm">
          Confirm
        </a-button>
      </div>
    </a-modal>
  </div>
</template>

<script setup>
import moment from 'moment-timezone'
import { message } from 'ant-design-vue'
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import { QuestionCircleFilled, WarningFilled } from '@ant-design/icons-vue'
import TableTitlePopover from '../../components/tableTitlePopover.vue'
import { apiDomain } from '../../../config'
import paymentsApi from '../../server/payments'
import { user } from '../../server/index'
import enGB from 'ant-design-vue/es/date-picker/locale/en_GB'

/** 支付相关日期统一使用美西时间 */
const PAYMENT_TIMEZONE = 'America/Los_Angeles'

const nowPt = () => moment.tz(PAYMENT_TIMEZONE)

/** 将日历选中的日期按美西自然日解析（避免浏览器本地时区偏移） */
const toPtDay = (input) => {
  if (input == null || input === '') return null
  const m = moment.isMoment(input) ? input : moment(input)
  return moment.tz(m.format('YYYY-MM-DD'), 'YYYY-MM-DD', PAYMENT_TIMEZONE)
}

/** 按美西时区格式化时间戳 */
const formatPt = (value, format = 'YYYY-MM-DD') => {
  if (value == null || value === '') return '-'
  return moment.tz(value, PAYMENT_TIMEZONE).format(format)
}

const isLoading = ref(false)

/** 当前打开 Adjust Popconfirm 的 paymentId（与行 record 对齐） */
const adjustPopPaymentId = ref(null)
const adjustTargetRecord = ref(null)
/** Select 下拉展开时，避免 Popconfirm 误判为外部点击而关闭 */
const adjustSelectDropdownOpen = ref(false)
const adjustForm = ref({
  type: 'add',
  amount: '',
  reason: '',
})

const payModalVisible = ref(false)
const paySubmitting = ref(false)
const payTargetRecord = ref(null)

const batchPayModalVisible = ref(false)
const batchPaySubmitting = ref(false)
const BATCH_PAY_DELAY_MS = 400

const hasPaymentAccount = (record) => {
  const account = record?.paymentAccount
  return account != null && String(account).trim() !== ''
}

/** 仅 Confirmed、有 paymentAccount 且未支付（paid 不为 true）的行可勾选 */
const isRowSelectable = (record) => {
  return (
    record?.status === 'Confirmed' &&
    !record.paid &&
    hasPaymentAccount(record)
  )
}

const filterSelectableRowKeys = (keys) =>
  keys.filter((key) => {
    const record = paymentsList.value.find((item) => String(item.paymentId) === String(key))
    return record && isRowSelectable(record)
  })

const payDisplay = computed(() => {
  const record = payTargetRecord.value || {}
  return {
    username: record.influencerUsername || record.userName || '-',
    paypalAccount: record.paymentAccount || '-',
    payoutAmount: formatCurrency(record.actualPayoutUsdForKol, 'USD'),
  }
})

const filterLabelCol = { span: 10 }

const statusOptions = [
  { label: 'All Status', value: '' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Confirmed', value: 'Confirmed' },
  { label: 'Paid', value: 'Paid' },
]

const filter = ref({
  influencerId: undefined,
  influencerEmail: '',
  status: 'Pending',
})
const influencerUsernameInput = ref('')
/** 支付日期：一条连续区间，起为「起始日所在周」的周一，止为「结束日所在周」的周日（若超过今天则用今天）；可跨多周 */
const filterDate = ref([])
/** 面板选择过程中 [start,end]，用于 disabledDate */
const pickingRange = ref([])

const disabledPaymentRangeDate = (current) => {
  if (!current) return false
  const c = toPtDay(current)
  const minStart = nowPt().subtract(2, 'years').startOf('day')
  // 超过两年前 / 晚于今天，不可选
  if (c.isBefore(minStart, 'day') || c.isAfter(nowPt().endOf('day'))) return true

  const pick = pickingRange.value || []
  const hasStart = !!pick[0]
  const hasEnd = !!pick[1]

  // 先选结束再选起始：起始不能选结束日及之后
  if (hasEnd && !hasStart) {
    return !c.startOf('day').isBefore(toPtDay(pick[1]).startOf('day'))
  }

  // 已选起始（对齐周一后）：结束须严格晚于起始周周一
  if (hasStart && !hasEnd) {
    const monday = toPtDay(pick[0]).startOf('isoWeek')
    return !c.isAfter(monday, 'day')
  }

  return false
}

const onPaymentRangeOpenChange = (open) => {
  if (!open) pickingRange.value = []
}

/**
 * 起始：raw 所在周周一；结束：raw 所在周周日，若晚于今天则截到今天结束
 */
const normalizePaymentWeekRange = (rawStart, rawEnd) => {
  const minStart = nowPt().subtract(2, 'years').startOf('day')
  let start = toPtDay(rawStart).startOf('isoWeek').startOf('day')
  if (start.isBefore(minStart, 'day')) {
    start = minStart.clone().startOf('isoWeek').startOf('day')
    // 若对齐周一后仍早于两年前，则从两年前当天起
    if (start.isBefore(minStart, 'day')) start = minStart.clone()
  }
  let end = toPtDay(rawEnd).endOf('isoWeek')
  const todayEod = nowPt().endOf('day')
  if (end.isAfter(todayEod)) end = todayEod.clone()
  if (!end.isAfter(start, 'day')) return null
  return [start, end]
}

const onPaymentRangeCalendarChange = (val) => {
  pickingRange.value = val && val.length ? [...val] : []
  // 选中起始：对齐到美西当周周一
  if (val?.[0] && !val[1]) {
    const mon = toPtDay(val[0]).startOf('isoWeek')
    nextTick(() => {
      if (filterDate.value?.[0] && !filterDate.value?.[1]) {
        filterDate.value = [mon, null]
      }
    })
  }
  // 选中结束：对齐到结束日所在周周日，且不超过美西今天
  if (val?.[0] && val?.[1]) {
    const start = toPtDay(val[0]).startOf('isoWeek').startOf('day')
    if (!toPtDay(val[1]).isAfter(start, 'day')) return
    let end = toPtDay(val[1]).endOf('isoWeek')
    const todayEod = nowPt().endOf('day')
    if (end.isAfter(todayEod)) end = todayEod.clone()
    nextTick(() => {
      if (filterDate.value?.[0] && filterDate.value?.[1]) {
        filterDate.value = [start, end]
      }
    })
  }
}

const onPaymentRangeChange = (val) => {
  pickingRange.value = []
  if (!val || !val[0] || !val[1]) {
    filterDate.value = val && val.length ? val : []
    return
  }
  const norm = normalizePaymentWeekRange(val[0], val[1])
  if (!norm) {
    message.warning('Invalid range: end must be after the start week (Monday).')
    filterDate.value = []
    return
  }
  filterDate.value = norm
}

const paymentsList = ref([])
const pagination = ref({
  current: 1,
  pageSize: 50,
  total: 0,
  // 默认按 KOL Payment Period 降序
  sort: '-paymentPeriodStartTime',
})

const userNameSuggestions = ref([])

const columns = ref([
  {
    dataIndex: 'kolPaymentPeriod',
    key: 'KOL Payment Period',
    width: 210,
    titleTip: 'Weekly, aggregated by confirmation time.',
    sorter: true,
    sortOrder: 'descend',
  },
  {
    dataIndex: 'influencerUsername',
    key: 'Influencer Username',
    width: 150,
  },
  {
    dataIndex: 'influencerEmail',
    key: 'Influencer Email',
    width: 180,
  },
  {
    dataIndex: 'paymentAccount',
    key: 'PayPal Account',
    width: 200,
  },
  {
    dataIndex: 'affiliatePaymentAmountUsd',
    key: 'Payment Amount from Affiliate USD',
    width: 200,
    titleTip: 'Total commission payment we received from affiliate.',
    sorter: true,
  },
  {
    dataIndex: 'paymentAmountUsdForKol',
    key: 'Payment Amount for KOL USD',
    width: 200,
    titleTip: 'Amount payable to influencer.',
    sorter: true,
  },
  {
    dataIndex: 'adjustmentAmountUsdForKol',
    key: 'Adjustment Amount for KOL USD',
    width: 200,
    titleTip: 'Manual adjustment (positive or negative), editable when status is Pending for this KOL payment, locked after Confirmed.',
  },
  {
    dataIndex: 'actualPayoutUsdForKol',
    key: 'Actual Payout for KOL USD',
    width: 220,
    titleTip: 'Final payout amount after manual adjustment: Payout=Payment Amount for KOL USD ± Adjustment Amount for KOL USD.',
  },
  {
    dataIndex: 'status',
    key: 'Status',
    width: 110,
  },
  {
    dataIndex: 'note',
    key: 'Note',
    width: 260,
  },
  {
    key: 'Action',
    width: 160,
    fixed: 'right',
  },
])

const selectedRowKeys = ref([])

const selectedBatchRecords = computed(() =>
  selectedRowKeys.value
    .map((key) => paymentsList.value.find((item) => String(item.paymentId) === String(key)))
    .filter(Boolean)
)

const batchCheckoutColumns = [
  { title: 'ID', dataIndex: 'paymentId', key: 'paymentId', width: 72 },
  { title: 'KOL', dataIndex: 'influencerUsername', key: 'influencerUsername', ellipsis: true },
  { title: 'PayPal Account', dataIndex: 'paymentAccount', key: 'paymentAccount', ellipsis: true },
  {
    title: 'Payout',
    dataIndex: 'actualPayoutUsdForKol',
    key: 'actualPayoutUsdForKol',
    width: 120,
    align: 'right',
  },
]

const selectedCumulativeAmount = computed(() =>
  selectedRowKeys.value.reduce((sum, key) => {
    const record = paymentsList.value.find((item) => String(item.paymentId) === String(key))
    if (!record) return sum
    const amount = Number(record.actualPayoutUsdForKol)
    return sum + (Number.isNaN(amount) ? 0 : amount)
  }, 0)
)

const rowSelection = computed(() => ({
  selectedRowKeys: selectedRowKeys.value,
  onChange: (keys) => {
    selectedRowKeys.value = filterSelectableRowKeys(keys)
  },
  getCheckboxProps: (record) => ({
    disabled: !isRowSelectable(record),
  }),
}))

// 列 dataIndex 与后端 sort 字段的映射（KOL Payment Period 对应 paymentPeriodStartTime）
const getSortField = (col) =>
  col.dataIndex === 'kolPaymentPeriod' ? 'paymentPeriodStartTime' : col.dataIndex

watch(
  () => pagination.value.sort,
  (val) => {
    columns.value.forEach((col) => {
      if (!col.sorter || !col.dataIndex) return
      const sortField = getSortField(col)
      if (val === `-${sortField}`) {
        col.sortOrder = 'descend'
      } else if (val === `+${sortField}` || val === sortField) {
        col.sortOrder = 'ascend'
      } else {
        col.sortOrder = null
      }
    })
  },
  { immediate: true }
)

const tablePagination = computed(() => ({
  current: pagination.value.current,
  pageSize: pagination.value.pageSize,
  total: pagination.value.total,
  showSizeChanger: true,
}))

const onUserSearch = (value) => {
  const keyword = String(value || '').trim()
  if (!keyword) {
    userNameSuggestions.value = []
    return
  }
  user
    .getSuggestionList('userName', {
      userName: keyword,
      size: 20,
    })
    .then((res) => {
      if (res.success) {
        userNameSuggestions.value = res.data.map((item) => ({
          label: item.userName,
          value: item.id,
        }))
      }
    })
}

const onUserSelect = (id, option) => {
  filter.value.influencerId = id ?? undefined
  influencerUsernameInput.value = option?.label ?? ''
  handleSearch()
}

const changeUser = (e) => {
  influencerUsernameInput.value = String(e?.target?.value ?? '').trim()
}

const clearUser = () => {
  filter.value.influencerId = undefined
  influencerUsernameInput.value = ''
  userNameSuggestions.value = []
  handleSearch()
}

const buildParams = () => {
  const params = {
    type: 'pendingPayment',
    page: pagination.value.current,
    size: pagination.value.pageSize,
    status: filter.value.status || undefined,
    influencerId: filter.value.influencerId,
    influencerEmail: filter.value.influencerEmail || undefined,
  }

  if (filterDate.value?.length === 2) {
    params.paymentPeriodStartTime = filterDate.value[0]?.valueOf()
    params.paymentPeriodEndTime = filterDate.value[1]?.valueOf()
  }

  // 排序：与 dmReceived 一致，使用 encodeURIComponent 的 sort 字符串
  if (pagination.value.sort) {
    params.sort = encodeURIComponent(pagination.value.sort)
  }

  Object.keys(params).forEach((key) => {
    if (
      params[key] === null ||
      params[key] === undefined ||
      params[key] === ''
    ) {
      delete params[key]
    }
  })

  return params
}

const fetchList = () => {
  isLoading.value = true
  const params = buildParams()

  paymentsApi
    .getList(params)
    .then((res) => {
      isLoading.value = false
      const list = res?.list || res?.records || res?.data || []
      paymentsList.value = Array.isArray(list) ? list : []
      pagination.value.total =
        res?.total || res?.dataTotal || paymentsList.value.length
      selectedRowKeys.value = filterSelectableRowKeys(selectedRowKeys.value)
    })
    .catch(() => {
      isLoading.value = false
    })
}

const handleSearch = () => {
  pagination.value.current = 1
  selectedRowKeys.value = []
  fetchList()
}

const handleReset = () => {
  filter.value = {
    influencerId: undefined,
    influencerEmail: '',
    status: 'Pending',
  }
  influencerUsernameInput.value = ''
  userNameSuggestions.value = []
  filterDate.value = []
  pickingRange.value = []
  pagination.value.current = 1
  selectedRowKeys.value = []
  handleSearch()
}

const handleTableChange = (pager, _filters, sorter) => {
  // 判断是否为分页器变化
  const isPagerChange = pager?.current !== pagination.value.current || pager?.pageSize !== pagination.value.pageSize

  if (isPagerChange) {
    if (pager?.current) {
      pagination.value.current = pager.current
    }
    if (pager?.pageSize) {
      pagination.value.pageSize = pager.pageSize
    }
  } else {
    let sortField = ''
    let defaultSort = '-kolPaymentPeriod'

    if (sorter && sorter.field) {
      // 字段映射
      switch (sorter.field) {
        case 'kolPaymentPeriod':
          sortField = 'paymentPeriodStartTime'
          break
        case 'affiliatePaymentAmountUsd':
          sortField = 'affiliatePaymentAmountUsd'
          break
        case 'paymentAmountUsdForKol':
          sortField = 'paymentAmountUsdForKol'
          break
        case 'actualPayoutUsdForKol':
          sortField = 'actualPayoutUsdForKol'
          break
        default:
          sortField = ''
      }

      if (sortField) {
        if (pagination.value.sort.endsWith(sortField)) {
          pagination.value.sort = (pagination.value.sort.startsWith('-') ? '+' : '-') + sortField
        } else {
          pagination.value.sort = `-${sortField}`
        }
      } else {
        pagination.value.sort = defaultSort
      }
    }
  }

  fetchList()
}

const handleConfirm = (record) => {
  paymentsApi.confirmPayment(record.paymentId).then(() => {
    message.success('Confirmed')
    fetchList()
  }).catch(() => {
    message.error('Failed to confirm')
  })
}

const closePayModal = () => {
  payModalVisible.value = false
  paySubmitting.value = false
  payTargetRecord.value = null
}

const openPayModal = (record) => {
  if (!record?.paymentId) return
  payTargetRecord.value = record
  payModalVisible.value = true
}

const openBatchPayModal = () => {
  if (!selectedRowKeys.value.length) return
  batchPayModalVisible.value = true
}

const closeBatchPayModal = () => {
  if (batchPaySubmitting.value) return
  batchPayModalVisible.value = false
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const handleBatchPayConfirm = async () => {
  const paymentIds = [...selectedRowKeys.value]
  if (!paymentIds.length || batchPaySubmitting.value) return
  batchPaySubmitting.value = true
  try {
    for (let i = 0; i < paymentIds.length; i++) {
      await paymentsApi.payPayments([paymentIds[i]])
      if (i < paymentIds.length - 1) {
        await sleep(BATCH_PAY_DELAY_MS)
      }
    }
    message.success('Batch payment submitted')
    batchPayModalVisible.value = false
    selectedRowKeys.value = []
    fetchList()
  } catch {
    message.error('Failed to batch pay')
  } finally {
    batchPaySubmitting.value = false
  }
}

const handlePayConfirm = () => {
  const record = payTargetRecord.value
  if (!record?.paymentId || paySubmitting.value) return
  paySubmitting.value = true
  return paymentsApi
    .payPayments([record.paymentId])
    .then(() => {
      message.success('Payment submitted')
      selectedRowKeys.value = selectedRowKeys.value.filter(
        (key) => String(key) !== String(record.paymentId)
      )
      closePayModal()
      fetchList()
    })
    .catch(() => {
      message.error('Failed to pay')
    })
    .finally(() => {
      paySubmitting.value = false
    })
}

const resetAdjustForm = () => {
  adjustForm.value = {
    type: 'add',
    amount: '',
    reason: '',
  }
}

const isAdjustPopOpen = (record) =>
  adjustPopPaymentId.value != null &&
  String(adjustPopPaymentId.value) === String(record.paymentId)

/** 下拉挂到 Popconfirm 容器内，避免被 overflow 裁剪或触发外部点击关闭 */
const getAdjustSelectPopupContainer = (trigger) =>
  trigger?.closest?.('.pending-adjust-popconfirm') || document.body

const onAdjustSelectDropdownVisibleChange = (open) => {
  adjustSelectDropdownOpen.value = open
}

const onAdjustPopOpenChange = (open, record) => {
  if (!open) {
    if (adjustSelectDropdownOpen.value) return
    adjustPopPaymentId.value = null
    adjustTargetRecord.value = null
    resetAdjustForm()
    return
  }
  adjustPopPaymentId.value = record.paymentId
  adjustTargetRecord.value = record
  resetAdjustForm()
}

/**
 * Popconfirm OK：须返回 Promise；reject 时弹层不关闭（与 ant ActionButton 行为一致）。
 */
const handleAdjustPopConfirm = () => {
  const target = adjustTargetRecord.value
  if (!target?.paymentId) {
    return Promise.reject(new Error(''))
  }
  const amountValue = Number(adjustForm.value.amount)
  if (!adjustForm.value.amount || Number.isNaN(amountValue) || amountValue <= 0) {
    message.error('Please enter a positive amount')
    return Promise.reject(new Error('validation'))
  }
  const reason = String(adjustForm.value.reason ?? '').trim()
  if (!reason) {
    message.error('Please enter a reason')
    return Promise.reject(new Error('validation'))
  }
  return paymentsApi
    .adjustmentAmountUsdForKol({
      paymentId: target.paymentId,
      amount: adjustForm.value.type === 'subtract' ? -amountValue : amountValue,
      reason,
    })
    .then(() => {
      message.success('Adjustment submitted')
      fetchList()
      adjustPopPaymentId.value = null
      adjustTargetRecord.value = null
      resetAdjustForm()
    })
    .catch(() => {
      message.error('Failed to adjust')
      return Promise.reject(new Error('api'))
    })
}

const handleNoteChange = (record, value) => {
  record.note = value
  paymentsApi.addNote({ type: 'pendingPayment', paymentId: record.paymentId, note: value }).then(() => {
    message.success('Note saved')
  })
}

const formatCurrency = (value, currency) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  const num = Number(value)
  if (Number.isNaN(num)) {
    return String(value)
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)
  } catch (e) {
    return `${currency || ''} ${num.toFixed(2)}`.trim()
  }
}

const exportUrl = computed(() => {
  const params = []
  if (filterDate.value?.length === 2) {
    params.push(`paymentPeriodStartTime=${filterDate.value[0]?.valueOf()}`)
    params.push(`paymentPeriodEndTime=${filterDate.value[1]?.valueOf()}`)
  }
  if (filter.value.influencerId) {
    params.push(`influencerId=${filter.value.influencerId}`)
  }
  if (filter.value.influencerEmail) {
    params.push(`influencerEmail=${filter.value.influencerEmail}`)
  }
  if (filter.value.status) {
    params.push(`status=${filter.value.status}`)
  }
  // 导出也带上当前排序，保持与列表一致
  if (pagination.value.sort) {
    params.push(`sort=${encodeURIComponent(pagination.value.sort)}`)
  }
  const query = params.length ? `?${params.join('&')}` : ''
  return `https://${apiDomain}/admin/influencer-marketing/v1/finance/kol/payments/export${query}`
})

onMounted(() => {
  fetchList()
})
</script>

<style scoped>
.table-wrapper :deep(.ant-select-dropdown) {
  width: auto !important;
  /* 关键：宽度自动 */
  min-width: 100%;
  /* 可选：最小宽度和输入框一致，避免过窄 */
  max-width: none !important;
  /* 取消最大宽度限制 */
}

.filter-row-2 {
  margin-top: 16px;
}

.adjust-amount-row {
  display: flex;
  align-items: center;
}

.adjust-modal__footer {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.paypal-loading {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(0, 0, 0, 0.45);
}

.paypal-account-text {
  color: #1677ff;
  word-break: break-all;
}

.pending-batch-bar {
  position: fixed;
  right: 32px;
  bottom: 32px;
  z-index: 200;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 12px 10px 20px;
  background: #141a2e;
  border-radius: 999px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  color: #fff;
}

.pending-batch-bar__left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pending-batch-bar__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 999px;
  background: #1677ff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
}

.pending-batch-bar__label {
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
}

.pending-batch-bar__divider {
  width: 1px;
  height: 22px;
  background: rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.pending-batch-bar__amount {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  white-space: nowrap;
}

.pending-batch-bar__amount-label {
  color: rgba(255, 255, 255, 0.85);
}

.pending-batch-bar__amount-value {
  color: #52c41a;
  font-weight: 600;
}

.pending-batch-bar__btn {
  border-radius: 999px;
  padding-inline: 20px;
  font-weight: 500;
}

.action-paid {
  color: rgba(0, 0, 0, 0.35);
  font-size: 14px;
  cursor: default;
  user-select: none;
}
</style>
<style lang="less">
.pending-payment .filter.filter-align .ant-form-item-label {
  text-align: right !important;
}

.pending-payment .filter.filter-align .ant-form-item-label>label {
  justify-content: flex-end !important;
  text-align: right !important;
}

.pending-adjust-popconfirm {
  .ant-popover-inner {
    overflow: visible !important;
  }

  .ant-popconfirm-message {
    overflow: visible;
  }

  .ant-popconfirm-message-title {
    font-weight: 600;
  }

  .pending-adjust-popconfirm__form {
    margin-top: 8px;
  }

  .pending-adjust-popconfirm__field {
    margin-bottom: 12px;
  }

  .pending-adjust-popconfirm__field:last-child {
    margin-bottom: 0;
  }

  .pending-adjust-popconfirm__label {
    font-size: 14px;
    margin-bottom: 6px;
    color: rgba(0, 0, 0, 0.88);
  }
}

.pending-adjust-select-dropdown {
  z-index: 1070 !important;
}

.kol-payout-confirm-modal {
  .ant-modal-content {
    border-radius: 12px;
    padding: 24px 28px 20px;
  }

  .ant-modal-body {
    padding: 0;
  }

  .kol-payout-confirm-modal__title {
    font-size: 18px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
    margin-bottom: 12px;
  }

  .kol-payout-confirm-modal__question {
    margin: 0 0 20px;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
    line-height: 22px;
  }

  .kol-payout-confirm-modal__panel {
    background: #fafafa;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 16px;
  }

  .kol-payout-confirm-modal__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    font-size: 14px;
    line-height: 22px;

    &+.kol-payout-confirm-modal__row {
      margin-top: 12px;
    }
  }

  .kol-payout-confirm-modal__label {
    color: rgba(0, 0, 0, 0.45);
    flex-shrink: 0;
  }

  .kol-payout-confirm-modal__value {
    text-align: right;
    word-break: break-all;
  }

  .kol-payout-confirm-modal__value--strong {
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
  }

  .kol-payout-confirm-modal__value--link {
    color: #1677ff;
    font-weight: 500;
  }

  .kol-payout-confirm-modal__value--amount {
    font-weight: 600;
    color: #52c41a;
    font-size: 16px;
  }

  .kol-payout-confirm-modal__warning {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 24px;
  }

  .kol-payout-confirm-modal__warning-icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 20px;
    font-size: 16px;
    line-height: 20px;
    color: #faad14;
  }

  .kol-payout-confirm-modal__warning-text {
    flex: 1;
    color: #ff4d4f;
    font-size: 13px;
    line-height: 20px;
  }

  .kol-payout-confirm-modal__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
}

.batch-checkout-modal {
  .ant-modal-content {
    border-radius: 12px;
    padding: 24px 28px 20px;
  }

  .ant-modal-body {
    padding: 0;
  }

  .batch-checkout-modal__title {
    font-size: 18px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.88);
    margin-bottom: 12px;
  }

  .batch-checkout-modal__desc {
    margin: 0 0 16px;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.65);
    line-height: 22px;

    strong {
      color: #1677ff;
      font-weight: 600;
    }
  }

  .batch-checkout-modal__table {
    margin-bottom: 16px;

    .ant-table-thead>tr>th {
      background: #fafafa;
      font-weight: 600;
    }
  }

  .batch-checkout-modal__paypal {
    color: #1677ff;
    word-break: break-all;
  }

  .batch-checkout-modal__payout {
    color: #52c41a;
    font-weight: 600;
  }

  .batch-checkout-modal__total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 18px;
    margin-bottom: 16px;
    background: #f6ffed;
    border: 1px solid #b7eb8f;
    border-radius: 8px;
  }

  .batch-checkout-modal__total-label {
    font-size: 14px;
    font-weight: 600;
    color: #389e0d;
  }

  .batch-checkout-modal__total-value {
    font-size: 22px;
    font-weight: 700;
    color: #389e0d;
    line-height: 1.2;
  }

  .batch-checkout-modal__notice {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 24px;
  }

  .batch-checkout-modal__notice-icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 20px;
    font-size: 16px;
    line-height: 20px;
    color: #faad14;
  }

  .batch-checkout-modal__notice-text {
    flex: 1;
    color: #ff4d4f;
    font-size: 13px;
    line-height: 20px;
  }

  .batch-checkout-modal__footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
}
</style>
